export type InAppNotificationKind =
  | 'new_ticket'
  | 'sla_warning'
  | 'assignment'
  | 'status_change';

export type InAppNotification = {
  id: string;
  kind: InAppNotificationKind;
  title: string;
  body: string;
  subject: string | null;
  ticketId: string;
  actor: string;
  read: boolean;
  createdAt: string;
};

export function timeAgo(iso: string) {
  if (!iso) return '';
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

export function isToday(iso: string) {
  if (!iso) return false;
  const d = new Date(iso);
  const t = new Date();
  return (
    d.getFullYear() === t.getFullYear() &&
    d.getMonth() === t.getMonth() &&
    d.getDate() === t.getDate()
  );
}

/** Strip emoji / decorative symbols from API messages for a clean UI. */
export function sanitizeNotificationText(text: string): string {
  if (!text) return '';
  return text
    .replace(/[\u{1F300}-\u{1FAFF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}\u{FE00}-\u{FE0F}\u{200D}]/gu, '')
    .replace(/\s{2,}/g, ' ')
    .trim();
}

function extractTicketRef(...texts: string[]): string | null {
  for (const t of texts) {
    const m = t.match(/\b(TKT|TXT)-\d+\b/i);
    if (m) return m[0].toUpperCase();
  }
  return null;
}

function normalizeKind(raw: unknown, title = ''): InAppNotificationKind {
  const s = `${String(raw ?? '')} ${title}`.toLowerCase().replace(/[\s-]+/g, '_');
  if (s.includes('sla')) return 'sla_warning';
  if (s.includes('assign')) return 'assignment';
  if (s.includes('status') || s.includes('resolved') || s.includes('closed')) return 'status_change';
  if (s.includes('new') || s.includes('create')) return 'new_ticket';
  return 'new_ticket';
}

export function isServiceDeskNotification(r: Record<string, unknown>): boolean {
  const extra = r.extra;
  if (!extra || typeof extra !== 'object') return false;
  const app = String((extra as Record<string, unknown>).app ?? '').toLowerCase();
  return app === 'servicedesk';
}

export function parseInAppNotifications(raw: unknown): InAppNotification[] {
  if (!raw) return [];
  const obj = raw as Record<string, unknown>;
  const arr = Array.isArray(raw)
    ? raw
    : Array.isArray(obj.notifications)
      ? obj.notifications
      : Array.isArray(obj.results)
        ? obj.results
        : Array.isArray(obj.data)
          ? obj.data
          : [];

  return arr
    .filter(isServiceDeskNotification)
    .map((r: Record<string, unknown>, index: number) => {
      const extra =
        r.extra && typeof r.extra === 'object'
          ? (r.extra as Record<string, unknown>)
          : {};
      const title = sanitizeNotificationText(
        String(r.title ?? r.notification_title ?? r.subject ?? 'Notification')
      );
      const body = sanitizeNotificationText(
        String(r.message ?? r.body ?? r.description ?? r.content ?? '')
      );
      const ticketId = String(
        extra.ui_id ??
          extra.ticket_number ??
          r.ticket_id ??
          extractTicketRef(title, body) ??
          '—'
      );
      const actor = String(
        extra.actor ??
          extra.requester_name ??
          extra.created_by ??
          r.actor ??
          r.created_by ??
          r.sender ??
          r.user_name ??
          'System'
      );
      const read = Boolean(r.is_read ?? r.read ?? r.isRead ?? false);
      const createdAt = String(
        r.created_on ?? r.created_at ?? r.timestamp ?? r.createdAt ?? ''
      );
      // Prefer extra.subject (direct field); for sla_escalation it's embedded in message
      const subjectFromExtra =
        typeof extra.subject === 'string' && extra.subject.trim()
          ? extra.subject.trim()
          : null;
      const subjectFromBody =
        body.match(/subject:\s*(.+?)\)/i)?.[1]?.trim() ??
        body.match(/[-\u2013]\s*[\u201c\u201d"](.+?)[\u201c\u201d"]/)?.[1]?.trim() ??
        null;
      const subject = subjectFromExtra ?? subjectFromBody;

      return {
        id: String(r.id ?? r.notification_id ?? `notification-${index}`),
        kind: normalizeKind(extra.type ?? r.notification_type ?? r.type ?? r.category, title),
        title,
        body,
        subject,
        ticketId,
        actor,
        read,
        createdAt,
      };
    });
}

export function parseNotificationCount(raw: unknown, ...keys: string[]): number {
  if (!raw || typeof raw !== 'object') return 0;
  const o = raw as Record<string, unknown>;
  for (const key of keys) {
    const v = o[key];
    if (typeof v === 'number' && Number.isFinite(v)) return Math.max(0, Math.floor(v));
    if (typeof v === 'string' && v.trim() !== '') {
      const n = Number.parseInt(v, 10);
      if (!Number.isNaN(n)) return Math.max(0, n);
    }
  }
  return 0;
}
